/**
 * The Vendor Tile's own rules (piece 24): the corporate exemption and the
 * legal-services override, the four W-9 states, TIN validation and the type
 * derived from the value, Filing Status, backup withholding and the
 * ten-return e-filing count.
 *
 * 🔨 Every rule here is pure. The tile renders what these answer, the service
 * writes what they permit, and neither invents a state of its own.
 */

import { digitsOf, validateTaxId } from '../validators';

export const VENDOR_ENTITY_TYPES = ['Individual', 'Partnership', 'CCorporation', 'SCorporation', 'LLC', 'TrustEstate'] as const;
export type VendorEntityTypeValue = (typeof VENDOR_ENTITY_TYPES)[number];

export const TIN_TYPES = ['SSN', 'ITIN', 'EIN'] as const;
export type TinTypeValue = (typeof TIN_TYPES)[number];

export const W9_STATUSES = ['NoW9OnFile', 'OverrideActive', 'UploadedPendingApproval', 'Approved'] as const;
export type W9StatusValue = (typeof W9_STATUSES)[number];

export const FILING_STATUSES = ['NotRequired', 'ThresholdPending', 'ReadyToFile', 'Filed', 'NeedsCorrection'] as const;
export type FilingStatusValue = (typeof FILING_STATUSES)[number];

export const BACKUP_WITHHOLDING_SOURCES = ['NotSet', 'MissingOrInvalidTin', 'IRSNotice'] as const;
export type BackupWithholdingSourceValue = (typeof BACKUP_WITHHOLDING_SOURCES)[number];

/** Settings › Tax › 1099 Filing Threshold: one effective-dated row, seeded 2,000 dollars in cents. */
export const FORM_1099_THRESHOLD_KEY = 'tax.form1099ThresholdAmount';
export const FORM_1099_THRESHOLD_DEFAULT_CENTS = 200_000;

/** 🔨 E-filing becomes mandatory once the organisation's aggregate information returns reach ten for the year. */
export const EFILE_MANDATORY_COUNT = 10;

/**
 * 🔨 Corporations are excluded from the Filing Status calculation by default.
 * An LLC that elected corporate treatment on its W-9 is recorded under
 * `CCorporation` or `SCorporation` and is exempt with them, so a row reading
 * `LLC` is always eligible.
 */
export function isCorporate(entityType: VendorEntityTypeValue): boolean {
  return entityType === 'CCorporation' || entityType === 'SCorporation';
}

/**
 * 🔨 Payments for legal services are reportable even to a corporation, and the
 * Legal Services flag overrides the corporate exemption. 💰 Both halves fail
 * quietly: without the exemption the system files for every incorporated
 * vendor, and without the override it files none for the law firm — and the
 * second is the case that draws a penalty.
 */
export function isReportableVendor(facts: { entityType: VendorEntityTypeValue; isLegalServices: boolean }): boolean {
  if (facts.isLegalServices) return true;
  return !isCorporate(facts.entityType);
}

// ---- The four W-9 states ----

/**
 * 🔨 W-9 Status is derived and never set by hand, and it is recalculated
 * whenever the W-9's own upload or approval state changes. An override in
 * effect wins over an absent file; an approved file wins over everything.
 */
export function w9StatusOf(facts: { w9FileId: string | null; approvedAt: Date | string | null; overrideReason: string | null }): W9StatusValue {
  if (facts.w9FileId && facts.approvedAt) return 'Approved';
  if (facts.w9FileId) return 'UploadedPendingApproval';
  if (facts.overrideReason?.trim()) return 'OverrideActive';
  return 'NoW9OnFile';
}

/**
 * 🔨 A vendor with no W-9 on file and no override cannot be marked active for
 * payment, at any year-to-date value including zero. The block starts at the
 * first payment, never at the reporting threshold. 💰 It was once gated on the
 * threshold, which let a vendor be paid $1,999 with no W-9 and inverted the
 * control's purpose.
 */
export function payableProblem(w9Status: W9StatusValue): string | null {
  if (w9Status === 'NoW9OnFile') return 'this vendor has no W-9 on file and no override, so they cannot be marked active for payment; the block starts at the first payment, not at the reporting threshold';
  return null;
}

/** 🔨 An override names a typed reason, and the reason renders wherever the status does — never a bare "overridden". */
export function overrideProblem(reason: string | null | undefined): string | null {
  return reason?.trim() ? null : 'an override names why it is in effect, and the reason is shown on the Vendor Tile wherever the status renders';
}

/** 🔨 Rejecting a W-9 bounces it back to No W-9 on File with a required rejection reason, prompting re-upload. */
export function rejectionProblem(reason: string | null | undefined): string | null {
  return reason?.trim() ? null : 'rejecting a W-9 names why, because the vendor is asked to upload another';
}

// ---- TIN validation ----

/** The dash pattern offered from the Entity Type; the uploader is never asked to pick a TIN type. */
export function tinPatternFor(entityType: VendorEntityTypeValue): string {
  return entityType === 'Individual' ? 'XXX-XX-XXXX' : 'XX-XXXXXXX';
}

/**
 * 🔨 `tinType` is derived from the shape and the area number of the value
 * entered, never typed and never taken from `entityType` alone. A value in the
 * EIN shape takes EIN whatever the Entity Type, which is how a sole proprietor
 * with a real EIN is recorded; an SSN-shaped value takes ITIN when its area
 * number is 900-999 and SSN otherwise.
 */
export function tinTypeOf(value: string, entityType: VendorEntityTypeValue): TinTypeValue {
  const trimmed = value.trim();
  const digits = digitsOf(trimmed);
  // The typed dashes say which shape was meant; an undashed value falls back to the Entity Type's own pattern.
  const einShaped = /^\d{2}-\d{7}$/.test(trimmed) || (!trimmed.includes('-') && entityType !== 'Individual');
  if (einShaped) return 'EIN';
  const area = Number(digits.slice(0, 3));
  return area >= 900 && area <= 999 ? 'ITIN' : 'SSN';
}

export interface TinVerdict {
  ok: boolean;
  tinType: TinTypeValue | null;
  /** What the tile renders: the last four digits behind the shape's own mask. */
  masked: string | null;
  /** The refusal, where the value is refused on save. */
  message: string | null;
  /** 💰 An ITIN is recorded and flagged for the uploader to confirm, never refused. */
  confirm: string | null;
}

/**
 * 🔨 TIN validation rejects on save, not just visually masks, in four cases:
 * the number is not exactly 9 digits, any group is all zeros, an SSN or ITIN
 * area number is 666, or an EIN carries a prefix the Field Format Validator
 * does not know. 💰 Format validation confirms shape and catches typos and
 * obviously fake numbers; it never proves the TIN belongs to this vendor.
 */
export function validateTin(value: string, entityType: VendorEntityTypeValue): TinVerdict {
  const trimmed = (value ?? '').trim();
  const digits = digitsOf(trimmed);
  if (digits.length !== 9) return { ok: false, tinType: null, masked: null, message: 'every taxpayer identification number is 9 digits', confirm: null };
  const tinType = tinTypeOf(trimmed, entityType);
  const groups = tinType === 'EIN' ? [digits.slice(0, 2), digits.slice(2)] : [digits.slice(0, 3), digits.slice(3, 5), digits.slice(5)];
  if (groups.some((group) => /^0+$/.test(group))) return { ok: false, tinType, masked: null, message: 'no group of a taxpayer identification number is all zeros', confirm: null };
  if (tinType !== 'EIN' && digits.slice(0, 3) === '666') return { ok: false, tinType, masked: null, message: 'an area number of 666 is never issued', confirm: null };
  if (tinType === 'EIN') {
    // 🔨 The prefix check is the Field Format Validator's, declared in Foundational data types and the seams; there is no second list.
    const outcome = validateTaxId(digits, 'US');
    if (!outcome.ok) return { ok: false, tinType, masked: null, message: outcome.message, confirm: null };
  }
  return {
    ok: true,
    tinType,
    masked: maskTin(digits, tinType),
    message: null,
    // 💰 900-999 is the ITIN range, so the value is recorded and the uploader is asked to confirm rather than refused.
    confirm: tinType === 'ITIN' ? 'the area number is in the ITIN range, so this is recorded as an ITIN; confirm that is what the W-9 says' : null,
  };
}

/** 🔨 `tinMasked` is what the tile renders; the number itself is an Accounting-classed gated field and never printed. */
export function maskTin(value: string, tinType: TinTypeValue): string {
  const digits = digitsOf(value);
  const tail = digits.slice(-4);
  return tinType === 'EIN' ? `••-•••${tail}` : `•••-••-${tail}`;
}

// ---- Filing Status and the reporting figures ----

/** One allocation the year-to-date figure reads: what it settled and whether a processor was behind the payment. */
export interface ReportablePayment {
  amountCents: number;
  /** The charge at the gateway; a payment carrying one is excluded, because the processor files Form 1099-K on the same money. */
  processorRef: string | null;
}

export interface YearToDateReportable {
  reportableCents: number;
  /** Shown beside the reportable total, with the reason, so the exclusion is visible rather than a missing figure. */
  excludedCents: number;
  excludedReason: string | null;
}

/**
 * 🔨 YTD Reportable Payments counts every allocation whose settling `Payment`
 * carries no `processorRef`, and excludes the rest. A card or gateway payment
 * carrying one is excluded because the processor files Form 1099-K on the same
 * money; a card payment keyed by hand with no reference stays reportable, and
 * so does a wire recorded as `Other`.
 */
export function yearToDateReportable(payments: readonly ReportablePayment[]): YearToDateReportable {
  const reportableCents = payments.filter((row) => !row.processorRef).reduce((sum, row) => sum + row.amountCents, 0);
  const excludedCents = payments.filter((row) => row.processorRef).reduce((sum, row) => sum + row.amountCents, 0);
  return {
    reportableCents,
    excludedCents,
    excludedReason: excludedCents > 0 ? 'settled through a payment processor, which files Form 1099-K on the same money' : null,
  };
}

/**
 * The Filing Status a vendor's year stands at, before anybody files. 🔨 A
 * vendor the corporate exemption covers is Not Required whatever they were
 * paid; an eligible vendor under the threshold is Threshold Pending, and one
 * at or over it is Ready to File.
 */
export function filingStatusFor(facts: { entityType: VendorEntityTypeValue; isLegalServices: boolean; reportableCents: number; thresholdCents: number }): FilingStatusValue {
  if (!isReportableVendor(facts)) return 'NotRequired';
  return facts.reportableCents >= facts.thresholdCents ? 'ReadyToFile' : 'ThresholdPending';
}

/** 🔨 Filing is recorded rather than performed: a return moves to Filed only when a person types how and when it was filed. */
export function filedProblem(filingMethod: string | null | undefined, filedOn: string | null | undefined): string | null {
  if (!filingMethod?.trim()) return 'a return is marked Filed by naming how it was filed; the system renders no 1099-NEC and submits no e-file';
  if (!filedOn || !/^\d{4}-\d{2}-\d{2}$/.test(filedOn)) return 'a return is marked Filed by naming the day it was filed, as YYYY-MM-DD';
  return null;
}

/** 🔨 The system renders no 1099-NEC and submits no IRS e-file; it keeps the figures and the record of what somebody filed elsewhere. */
export function renderRefusal(): string {
  return 'this system renders no 1099-NEC and submits no IRS e-file: it keeps the figures, the thresholds and the record of what was filed, and a person files through the IRS or a filing service';
}

/**
 * 🔨 E-filing becomes mandatory once the organisation's aggregate information
 * returns reach ten for the year, of every return type. 💰 The failure this
 * guards is a filing-method failure and not a reporting one: correct returns
 * submitted the wrong way, discovered after the deadline.
 */
export function eFilingMandatory(returnCount: number): boolean {
  return returnCount >= EFILE_MANDATORY_COUNT;
}

export function eFilingNotice(returnCount: number): string | null {
  if (!eFilingMandatory(returnCount)) return null;
  return `this organization has ${returnCount} information returns for the year, so e-filing is mandatory; a paper filing after the deadline is the failure this notice exists to prevent`;
}

// ---- Backup withholding ----

/**
 * 🔨 Backup Withholding Required is set by a missing or invalid TIN or an IRS
 * notice, never by the reporting threshold. It withholds 24% of every
 * reportable payment from the first dollar. 🔨 Crossing the threshold sets the
 * flag on nobody.
 */
export function backupWithholdingFor(facts: { tin: string | null; tinValid: boolean; irsNotice: boolean }): { required: boolean; source: BackupWithholdingSourceValue } {
  if (facts.irsNotice) return { required: true, source: 'IRSNotice' };
  if (!facts.tin || !facts.tinValid) return { required: true, source: 'MissingOrInvalidTin' };
  return { required: false, source: 'NotSet' };
}

export function backupWithholdingSourceProblem(source: string): string | null {
  if (!(BACKUP_WITHHOLDING_SOURCES as readonly string[]).includes(source)) return `the cause is ${BACKUP_WITHHOLDING_SOURCES.join(', ')}; the reporting threshold is never one of them`;
  return null;
}
